import type { ReactNode } from 'react';

export interface ChartTooltipZeile {
  label: ReactNode;
  /** Schon formatiert: `formatEuro(…)`, `formatZahl(…)`. */
  value: ReactNode;
}

export interface ChartTooltipProps<T> {
  /** Von recharts gesetzt, wenn das Element als `content` an `<Tooltip>` geht. */
  active?: boolean;
  /** Von recharts gesetzt; `payload[0].payload` ist der Datenpunkt. */
  payload?: ReadonlyArray<{ payload?: T }>;
  /** Kopfzeile aus dem Datenpunkt, z. B. der Name der Stufe. */
  titel: (punkt: T) => ReactNode;
  /** Zeilen darunter: Name links, Wert rechts mit tabellarischen Ziffern. */
  zeilen: (punkt: T) => ChartTooltipZeile[];
}

/**
 * Tooltip für Diagramme in der Optik eines Popovers. Strukturell typisiert,
 * damit elbklar-ui nicht von recharts abhängt: Die App gibt ihn als
 * `<Tooltip content={<ChartTooltip titel={…} zeilen={…} />} />`, recharts
 * reicht `active` und `payload` hinein. Farben der Reihen kommen aus
 * `var(--chart-1)` bis `var(--chart-5)`, nie als Hex-Wert.
 */
export function ChartTooltip<T>({ active, payload, titel, zeilen }: ChartTooltipProps<T>) {
  const punkt = payload?.[0]?.payload;
  if (!active || punkt === undefined) return null;
  return (
    <div className="min-w-40 rounded-lg border border-border bg-popover px-3 py-2.5 text-popover-foreground shadow-md">
      <p className="t-body-strong">{titel(punkt)}</p>
      <dl className="mt-1 flex flex-col gap-0.5">
        {zeilen(punkt).map((z, i) => (
          <div key={i} className="flex items-baseline justify-between gap-4 t-small">
            <dt className="text-muted-foreground">{z.label}</dt>
            <dd className="font-medium tabular">{z.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
